import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { fullMenu } from "../mock";

const formatPrice = (price) => price == null ? "—" : typeof price === "number" ? `$${price.toFixed(2)}` : price;

export default function Menu({
  menuData = fullMenu,
  title = <>The Menus — <em className="italic text-spice-gold font-light">Something for Every Tide</em></>,
  subtitle = "Every dish is made to order from our open kitchen. Please tell our team about any allergies or dietary requirements.",
  showTableMenuDownload = true,
  categoryNotes = {},
  downloadMenuUrl = "/image/siteimages/menu/mainmenu/Sea%20Spice%20Kitchen%20-%20Premium%20Table%20Menu%20with%20Photos%20(HD%20Print).pdf",
  downloadLabel = "Download Table Menu (PDF)",
  heroImage = "/image/siteimages/gallery/2880x2304Selects/SeaSpiceKitchen_Cover-1_2880x2304.jpg"
}) {
  const categories = Object.entries(menuData).flatMap(([section, sectionCategories], sectionIndex) =>
    Object.entries(sectionCategories).map(([name, items], categoryIndex) => ({
      id: `menu-${sectionIndex}-${categoryIndex}`,
      section,
      name,
      items
    }))
  );
  const navigationItems = Object.entries(menuData).flatMap(([section, sectionCategories], sectionIndex) => [
    { id: `menu-section-${sectionIndex}`, name: section, isSection: true },
    ...Object.entries(sectionCategories).map(([name], categoryIndex) => ({
      id: `menu-${sectionIndex}-${categoryIndex}`,
      name,
      isSection: false
    }))
  ]);
  const [active, setActive] = useState(navigationItems[0]?.id);

  const scrollToCategory = (category) => {
    setActive(category.id);
    const element = document.getElementById(category.id);
    if (element) {
      window.scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - 180, behavior: "smooth" });
    }
  };

  return (
    <>
      <section
        className="relative h-[55vh] min-h-[420px] flex items-center justify-center text-center bg-cover bg-center"
        style={{
          backgroundImage:
            `linear-gradient(rgba(10,36,36,0.72), rgba(10,36,36,0.88)), url(${heroImage})`
        }}
      >
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative pt-20">
          <span className="divider-ornament text-spice-gold">Sea Spice Kitchen</span>
          <h1 className="font-display text-white text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight mt-5">
            {title}
          </h1>
          <p className="text-white/75 mt-4 max-w-xl mx-auto text-base px-5">
            {subtitle}
          </p>
        </div>
      </section>

      {showTableMenuDownload && <div className="relative z-20 -mt-6 pb-4 text-center">
        <a
          href={downloadMenuUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center bg-spice-gold hover:bg-[hsl(38_75%_46%)] text-[#0d2929] font-semibold tracking-wide px-8 h-12 transition-colors"
        >
          {downloadLabel}
        </a>
      </div>}

      <div className="sticky top-20 lg:top-24 z-40 bg-cream/95 backdrop-blur border-b border-sea-deep/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 flex gap-2 lg:gap-3 overflow-x-auto py-4 scrollbar-hide">
          {navigationItems.map((category) => (
            <button
              key={category.id}
              onClick={() => scrollToCategory(category)}
              className={`whitespace-nowrap px-5 py-2 text-xs lg:text-sm tracking-[0.15em] uppercase border transition-all duration-300 ${
                active === category.id
                  ? "bg-sea-deep text-spice-gold border-sea-deep"
                  : category.isSection
                    ? "bg-spice-gold/15 text-sea-deep border-spice-gold/60 hover:bg-spice-gold/25"
                    : "bg-transparent text-sea-mid border-sea-deep/20 hover:border-spice-gold hover:text-spice-gold"
              }`}
            >
              {category.isSection ? `All ${category.name}` : category.name}
            </button>
          ))}
        </div>
      </div>

      <section className="py-20 lg:py-24 bg-cream">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          {Object.entries(menuData).map(([section, sectionCategories], sectionIndex) => (
            <div key={section} id={`menu-section-${sectionIndex}`} className="mb-20 scroll-mt-44">
              <div className="flex items-center gap-5 mb-10">
                <h2 className="font-display text-sea-deep text-3xl md:text-4xl lg:text-5xl font-medium">{section}</h2>
                <div className="flex-1 h-px bg-spice-gold/40" />
              </div>

              {Object.entries(sectionCategories).map(([categoryName, items], categoryIndex) => {
                const id = `menu-${sectionIndex}-${categoryIndex}`;
                const hasWinePrices = items.some((item) => item.glassPrice !== undefined || item.bottlePrice !== undefined);
                const hasHotBeveragePrices = items.some((item) => item.regularPrice !== undefined || item.largePrice !== undefined);

                return (
                  <div key={categoryName} id={id} className="mb-14 scroll-mt-44">
                    <div className="flex items-end justify-between gap-4 mb-7">
                      <h3 className="font-display text-sea-mid text-2xl md:text-3xl">{categoryName}</h3>
                      {hasWinePrices && <div className="flex gap-5 text-[10px] tracking-[0.2em] text-sea-mid/70 uppercase"><span>Glass</span><span>Bottle</span></div>}
                      {hasHotBeveragePrices && <div className="flex gap-5 text-[10px] tracking-[0.2em] text-sea-mid/70 uppercase"><span>Regular</span><span>Large</span></div>}
                    </div>
                    {categoryNotes[categoryName] && <p className="text-sea-mid/75 text-sm mb-6">{categoryNotes[categoryName]}</p>}
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-8">
                      {items.map((item) => (
                        <div key={item.name} className="group">
                          <div className="flex justify-between items-baseline gap-4 mb-2">
                            <h4 className="font-display text-sea-deep text-xl group-hover:text-spice-gold transition-colors">
                              {item.new && <span className="mr-2 text-xs font-sans font-semibold tracking-widest text-spice-amber">NEW</span>}
                              {item.signature && <span className="mr-2 text-spice-gold">★</span>}
                              {item.name} {item.glutenFree && <span className="text-sm text-sea-mid/70">(Gluten Free)</span>}
                            </h4>
                            <div className="flex-1 border-b border-dashed border-sea-deep/20 mb-2" />
                            {hasWinePrices ? (
                              <div className="flex gap-5 font-display text-spice-gold text-lg font-semibold whitespace-nowrap">
                                <span>{formatPrice(item.glassPrice)}</span>
                                <span>{formatPrice(item.bottlePrice)}</span>
                              </div>
                            ) : hasHotBeveragePrices ? (
                              <div className="flex gap-5 font-display text-spice-gold text-lg font-semibold whitespace-nowrap">
                                <span>{formatPrice(item.regularPrice)}</span>
                                <span>{formatPrice(item.largePrice)}</span>
                              </div>
                            ) : (
                              <span className="font-display text-spice-gold text-lg font-semibold">{formatPrice(item.price)}</span>
                            )}
                          </div>
                          {item.description && <p className="text-sea-mid/75 text-sm leading-relaxed pr-4">{item.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-sand">
        <div className="max-w-5xl mx-auto px-5 lg:px-10">
          <h2 className="font-display text-sea-deep text-3xl md:text-4xl mb-6">Allergies & Dietary Information</h2>
          <div className="text-sea-mid/80 leading-relaxed space-y-4 max-w-4xl">
            <p>Our chefs are happy to accommodate dietary requirements where possible. Please speak to our wait staff at the time of ordering.</p>
            <p>If you have any allergies or dietary requirements, please let our friendly team know before ordering. Our menu may contain common allergens including crustacean, egg, fish, lupin, milk, meat, mollusc, peanut, sesame, soy, tree nuts and wheat, which are prepared in a shared kitchen.</p>
            <p>Whilst all reasonable efforts are taken to accommodate our guests&apos; dietary needs, we cannot guarantee that our food will be allergen free.</p>
            <p>Menu items and prices are subject to change. Complimentary cake cutting is available.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 mt-9">
            <div className="border border-spice-gold/50 bg-cream p-6">
              <p className="text-spice-gold text-xs tracking-[0.2em] uppercase font-semibold mb-2">Kids Eat Free</p>
              <p className="text-sea-deep font-medium">Monday, Tuesday and Wednesday nights</p>
              <p className="text-sea-mid/75 text-sm mt-1">One free kids meal with every main meal.</p>
            </div>
            <div className="border border-spice-gold/50 bg-cream p-6">
              <p className="text-spice-gold text-xs tracking-[0.2em] uppercase font-semibold mb-2">Kids Half Price Weekends</p>
              <p className="text-sea-deep font-medium">Saturday and Sunday</p>
              <p className="text-sea-mid/75 text-sm mt-1">Half-price kids meal with every main meal.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-sea-deep text-center text-white">
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="font-display text-3xl md:text-4xl mb-4">Hungry for more?</h2>
          <p className="text-white/75 mb-7">Book your table and let our chefs take care of the rest.</p>
          <Link to="/reservations">
            <Button className="bg-spice-gold hover:bg-[hsl(38_75%_46%)] text-[#0d2929] font-semibold rounded-none px-10 h-14">Reserve a Table</Button>
          </Link>
        </div>
      </section>
    </>
  );
}
